import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa6';
import Button from './ui/Button';
import { PRICING_PLANS, goToApp, whatsappUrl } from '../constants';

const PlanCard = ({ plan, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: idx * 0.08 }}
    className={`relative rounded-2xl p-5 flex flex-col border ${plan.highlighted ? 'bg-violet-50 border-brand shadow-[0_20px_50px_-24px_rgba(124,58,237,0.7)]' : 'bg-white border-slate-200'}`}
    data-testid={`pricing-card-${idx}`}
  >
    {plan.highlighted && (
      <span className="absolute -top-3 left-5 bg-brand text-white text-[11px] font-bold px-3 py-1 rounded-full">Most Popular</span>
    )}
    {plan.savings && <span className="absolute -top-3 right-5 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">{plan.savings}</span>}
    <h3 className="font-display font-bold text-ink">{plan.name}</h3>
    <p className="text-xs text-slate-500 whitespace-nowrap">{plan.desc}</p>
    <p className="mt-4 font-display text-xl xl:text-2xl font-extrabold text-ink whitespace-nowrap" data-testid={`pricing-price-${idx}`}>{plan.price}</p>
    <p className="text-sm font-semibold text-brand">{plan.credits}</p>
    <ul className="mt-4 space-y-2 flex-1">
      {plan.features.map((f) => (
        <li key={f} className="flex gap-2 text-[13px] text-slate-600"><Check size={15} className="text-brand shrink-0 mt-0.5" />{f}</li>
      ))}
    </ul>
    <Button variant={plan.highlighted ? 'primary' : 'outlineLight'} size="sm" className="mt-5 w-full" onClick={goToApp} data-testid={`pricing-buy-now-${idx}`}>
      Get Started
    </Button>
    <a
      href={whatsappUrl(`Hi LoopAI, I have a question about the ${plan.name} (${plan.price}).`)}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 inline-flex items-center justify-center gap-1.5 text-[11px] whitespace-nowrap font-medium text-slate-500 hover:text-[#128C7E] transition-colors"
      data-testid={`pricing-whatsapp-${idx}`}
    >
      <FaWhatsapp size={14} className="text-[#25D366]" /> Questions? Chat on WhatsApp
    </a>
  </motion.div>
);

const Pricing = () => (
  <section id="pricing" className="py-20 md:py-24 bg-white scroll-mt-20" data-testid="section-pricing">
    <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-[260px_1fr] gap-10 items-start">
      <div className="lg:sticky lg:top-28">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand mb-3">Simple, flexible pricing</p>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-ink">Pay only for what you create.</h2>
        <p className="mt-3 text-slate-500">No long-term commitments. Choose a plan that fits your needs.</p>
        <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-violet-50 text-brand text-sm font-semibold px-4 py-2" data-testid="pricing-credit-note">1 Credit = ₹1 · Buy more, save more</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 pt-3">
        {PRICING_PLANS.map((plan, idx) => <PlanCard key={plan.name} plan={plan} idx={idx} />)}
      </div>
    </div>
  </section>
);

export default Pricing;
